import {
  isOwnerWish,
  type NonOwnerWish,
  type OwnerWish,
  type WishListItem,
  type WishPriority,
} from "../../types/wish";
import type { ReservationAction } from "../../types/reservation";

interface WishCardProps {
  wish: WishListItem;
  onEdit?: (wish: OwnerWish) => void;
  onDelete?: (wish: OwnerWish) => void;
  onReservationAction?: (wish: NonOwnerWish, action: ReservationAction) => void;
  reservationPending?: boolean;
  reorder?: {
    position: number;
    total: number;
    pending: boolean;
    onMove: (direction: -1 | 1) => void;
    onPointerDown: React.PointerEventHandler<HTMLButtonElement>;
    onPointerMove: React.PointerEventHandler<HTMLButtonElement>;
    onPointerUp: React.PointerEventHandler<HTMLButtonElement>;
    onPointerCancel: React.PointerEventHandler<HTMLButtonElement>;
  };
}

const priorityLabels: Record<WishPriority, string> = {
  high: "Высокий",
  medium: "Средний",
  low: "Низкий",
};

const priorityClasses: Record<WishPriority, string> = {
  high: "bg-[#FEF2F2] text-[#B91C1C]",
  medium: "bg-[#FFF0DF] text-[#C45D00]",
  low: "bg-[#F2F1EC] text-[#77776F]",
};

const statusLabels = {
  free: "Свободно",
  reserved: "Забронировано",
  purchased: "Куплено",
};

const statusClasses = {
  free: "bg-[#E5F4EA] text-[#287A45]",
  reserved: "bg-[#FFF0DF] text-[#C45D00]",
  purchased: "bg-[#E8E7F7] text-[#574DA3]",
};

function formatPrice(price: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 2,
  }).format(price);
}

export function WishCard({
  wish,
  onEdit,
  onDelete,
  onReservationAction,
  reservationPending = false,
  reorder,
}: WishCardProps) {
  const owner = isOwnerWish(wish);
  const isIdea = !wish.url;
  const showOwnerActions = owner && onEdit && onDelete;

  return (
    <article
      data-wish-id={wish.id}
      className="overflow-hidden rounded-[24px] border border-black/[0.04] bg-white shadow-[0_12px_32px_rgba(35,33,25,0.06)]"
    >
      {reorder && (
        <div role="group" className="flex min-w-0 items-center justify-end gap-1 border-b border-black/[0.05] px-3 py-2" aria-label={`Порядок: позиция ${reorder.position} из ${reorder.total}`}>
          <button
            type="button"
            disabled={reorder.pending || reorder.position === 1}
            onClick={() => reorder.onMove(-1)}
            className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl text-lg font-bold text-[#171714] hover:bg-[#F2F1EC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-not-allowed disabled:opacity-30"
            aria-label={`Переместить «${wish.title}» выше, позиция ${reorder.position} из ${reorder.total}`}
          >
            ↑
          </button>
          <button
            type="button"
            disabled={reorder.pending || reorder.position === reorder.total}
            onClick={() => reorder.onMove(1)}
            className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl text-lg font-bold text-[#171714] hover:bg-[#F2F1EC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-not-allowed disabled:opacity-30"
            aria-label={`Переместить «${wish.title}» ниже, позиция ${reorder.position} из ${reorder.total}`}
          >
            ↓
          </button>
          <button
            type="button"
            disabled={reorder.pending}
            onPointerDown={reorder.onPointerDown}
            onPointerMove={reorder.onPointerMove}
            onPointerUp={reorder.onPointerUp}
            onPointerCancel={reorder.onPointerCancel}
            className="flex h-11 min-w-11 shrink-0 touch-none cursor-grab items-center justify-center rounded-xl px-3 text-base font-bold text-[#77776F] hover:bg-[#F2F1EC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] active:cursor-grabbing disabled:cursor-wait disabled:opacity-40"
            aria-label={`Перетащить «${wish.title}», позиция ${reorder.position} из ${reorder.total}`}
          >
            ⠿
          </button>
        </div>
      )}
      <div className="flex gap-4 p-5">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[18px] bg-[#F2F1EC] text-3xl">
          {wish.image_url ? (
            <img
              src={wish.image_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <span aria-hidden="true">🎁</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold leading-tight tracking-[-0.025em] text-[#171714]">
              {wish.title}
            </h2>
            <div className="flex shrink-0 flex-wrap justify-end gap-1">
              {isIdea && (
                <span className="rounded-full bg-[#E8E7F7] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[#574DA3]">
                  Идея
                </span>
              )}
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] ${priorityClasses[wish.priority]}`}
              >
                {priorityLabels[wish.priority]}
              </span>
            </div>
          </div>
          {wish.event && (
            <p className="mt-2 text-xs font-semibold text-[#9A4900]">
              {wish.event.title}
            </p>
          )}
          {wish.price !== null && (
            <p className="mt-2 text-base font-extrabold text-[#171714]">
              {formatPrice(wish.price)}
            </p>
          )}
          {wish.description && (
            <p className="mt-2 line-clamp-2 text-sm leading-5 text-[#77776F]">
              {wish.description}
            </p>
          )}
          {isIdea && (
            <p className="mt-2 text-xs leading-5 text-[#77776F]">
              Это идея — покупатель сам выбирает конкретный товар.
            </p>
          )}
          {!owner && (
            <span className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClasses[wish.status]}`}>
              {statusLabels[wish.status]}
            </span>
          )}
        </div>
      </div>
      {(wish.url || showOwnerActions || !owner) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.05] px-4 py-2.5">
          {wish.url && (
            <a
              href={wish.url}
              target="_blank"
              rel="noreferrer"
              className="mr-auto rounded-xl px-1 py-2 text-sm font-semibold text-[#C45D00] hover:text-[#9A4900]"
            >
              Открыть ссылку
            </a>
          )}
          {showOwnerActions && (
            <>
              <button
                type="button"
                onClick={() => onEdit(wish)}
                className="cursor-pointer rounded-xl px-3 py-2 text-sm font-semibold text-[#171714] hover:bg-[#F2F1EC]"
              >
                Редактировать
              </button>
              <button
                type="button"
                onClick={() => onDelete(wish)}
                className="cursor-pointer rounded-xl bg-[#FEF2F2] px-3 py-2 text-sm font-semibold text-[#C83D32] hover:bg-[#FEE2E2]"
              >
                Удалить
              </button>
            </>
          )}
          {!owner && onReservationAction && (
            <div className="ml-auto flex flex-wrap justify-end gap-2" aria-busy={reservationPending}>
              {wish.can_reserve && (
                <button
                  type="button"
                  disabled={reservationPending}
                  onClick={() => onReservationAction(wish, "reserve")}
                  className="cursor-pointer rounded-xl bg-[#FF7A00] px-4 py-2 text-sm font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF7A00] disabled:cursor-wait disabled:bg-[#FDBA74]"
                >
                  Забронировать
                </button>
              )}
              {wish.can_cancel && (
                <button
                  type="button"
                  disabled={reservationPending}
                  onClick={() => onReservationAction(wish, "cancel")}
                  className="cursor-pointer rounded-xl bg-[#F2F1EC] px-3 py-2 text-sm font-semibold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-wait disabled:opacity-60"
                >
                  Отменить бронь
                </button>
              )}
              {wish.can_purchase && (
                <button
                  type="button"
                  disabled={reservationPending}
                  onClick={() => onReservationAction(wish, "purchase")}
                  className="cursor-pointer rounded-xl bg-[#171714] px-3 py-2 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-wait disabled:opacity-60"
                >
                  Куплено
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}
